import React, { FC, useEffect } from 'react';

import {
  adminActions,
  adminDeletedUserProfile,
  adminPharmacistResume,
  getAdminPharmacistResumes,
  getPharmacistDetails,
  HelpTypes,
  ICalendarEventType,
  PermissionsOfAdmin,
  selectMatchingAvailabilities,
  TypeOfUser,
} from '@pharmaplan/common';
import { Box } from '@mui/material';

import CollapsibleCard from '../../../../common/CollapsibleCard';
import { useAppDispatch } from '../../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../../hooks/useAppSelector';
import useGetItems from '../../../../../hooks/useGetItems';

import ProfileContent from '../../ProfileContent/ProfileContent';
import {
  IFromScreen,
  renderScreen,
  setBack,
  setFooterShow,
  setTitle,
} from '../../../../../actions/drawerActions';
import {
  AdminNotesType,
  OtherScreens,
  ScreenTypes,
  SecondaryAdminNavigation,
} from '../../../../../helpers/Constants';

import { pharmacistProfileConfig } from './helper';
import AdminPharmacistCard from './AdminPharmacistCard';
import usePharmacistProfileActions from '../../../../../hooks/PharmacistProfile/usePharmacistProfileActions';
import useAdminPermissions from '../../../../../hooks/Admin/useAdminPermissions';
import useDynamicProperties from '../../../../../hooks/useDynamicProperties';
import useSelfService from '../../../../../hooks/useSelfService';
import strings from '../../../../../localization';
import AdminNotes from '../../../AdminNotes';

export interface IPharmacistViewProfile {
  pharmacistId: string;
  type: ICalendarEventType;
  fromScreen?: IFromScreen;
  hasOwner?: boolean;
  showMenu?: boolean;
  enableActions?: boolean;
  back?: () => void;
  isExternal?: boolean;
}

const PharmacistViewProfile: FC<IPharmacistViewProfile> = ({
  pharmacistId,
  type,
  fromScreen,
  hasOwner,
  showMenu,
  enableActions,
  back,
  isExternal,
}) => {
  const UserLabels = {
    [HelpTypes.pharmacist]: strings.pharmacist,
    [HelpTypes.technicians]: strings.technician,
    [HelpTypes.students]: strings.pharmacyStudent,
    [HelpTypes.pharmacy]: strings.pharmacy,
    [HelpTypes.pharmacyAdmin]: strings.pharmacyAdministrator,
  };

  const dispatch = useAppDispatch();
  const { isSelfService } = useSelfService();

  const resume = useAppSelector(adminPharmacistResume);
  const { professionalUser, professionalUserDetails, capacityId, capacities } = useDynamicProperties();
  const matchingAvailabilities = useAppSelector(selectMatchingAvailabilities);

  const { can } = useAdminPermissions();
  const canActivate = can(PermissionsOfAdmin.Activation);

  const { workShiftId } = matchingAvailabilities ?? {};
  const { contactPreferences } = professionalUserDetails ?? {};

  const fromMatchingAvailabilities = fromScreen === OtherScreens.MatchingAvailabilities;
  const fromPendingActivations = fromScreen === SecondaryAdminNavigation.pendingActivation;
  const isDeletedPendingUser = fromScreen === OtherScreens.DeletedPendingUsers;
  const isDeletedPharmacistList = fromScreen === OtherScreens.DeletedPharmacistList;

  const isDeletedList = isDeletedPendingUser || isDeletedPharmacistList;

  const successAction = isDeletedList
    ? adminActions.deletedUserProfile
    : adminActions.getPharmacistDetails;

  const { preferdLanguage, selectedContactPreferences, userType } = professionalUser ?? {};

  const {
    getLanguageName,
    getPharmacistCapacities,
    getSelectedContactPreferences,
  } = useGetItems();
  const language = getLanguageName(preferdLanguage);
  const { handleButton } = usePharmacistProfileActions(HelpTypes.pharmacist);

  const pharmacistCapacity = getPharmacistCapacities(capacities, capacityId);
  const selectedPreferences = getSelectedContactPreferences(
    contactPreferences,
    selectedContactPreferences,
  );

  const gotoMatchingAvailabilities = () => {
    dispatch(
      renderScreen({
        screenNumber: 4,
        screenType: ScreenTypes.availableWorkshift,
        eventId: workShiftId,
        type,
      }),
    );
  };

  const handleBack = () => {
    if (fromMatchingAvailabilities) {
      dispatch(setBack(gotoMatchingAvailabilities));
    }
  };

  const initCall = () => {
    if (isDeletedList) {
      dispatch(
        adminDeletedUserProfile({
          userId: pharmacistId,
          approved: !!isDeletedPharmacistList,
        }),
      );
    } else {
      dispatch(getPharmacistDetails({ id: pharmacistId, isExternal }));
    }
  };

  useEffect(() => {
    handleBack();
    initCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workShiftId]);

  useEffect(() => {
    if (isSelfService) {
      dispatch(
        setTitle({
          title: strings.formatString(
            strings.profileDetailsFormat,
            UserLabels?.[
              (userType ?? TypeOfUser.pharmacist) as keyof typeof UserLabels
            ] ?? '',
          ),
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [UserLabels, userType]);

  useEffect(() => {
    if (fromPendingActivations && enableActions && canActivate) {
      handleButton(pharmacistId);
    } else {
      dispatch(setFooterShow(false));
    }

    dispatch(getAdminPharmacistResumes(pharmacistId));
    dispatch(setBack(back ?? null));

    return () => {
      dispatch(setBack(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openResume = () => {
    window.open(resume?.url ?? '', '_blank');
  };

  const openCertificate = (url: string) =>
    () => {
      window.open(url, '_blank');
    };

  return (
    <Box>
      <AdminPharmacistCard
        fromScreen={fromScreen}
        pharmacistId={pharmacistId}
        type={type}
        hasOwner={hasOwner}
        showMenu={showMenu}
        showActions
        isExternal={isExternal}
      />

      {!isDeletedList && (
        <CollapsibleCard
          title={strings.notes}
          keepExpanded
          content={(
            <Box sx={{ pt: 1 }}>
              <AdminNotes id={pharmacistId} notesType={AdminNotesType.pharmacist} />
            </Box>
          )}
        />
      )}

      {pharmacistProfileConfig({
        details: professionalUser,
        language,
        pharmacistCapacity,
        successAction,
        selectedPreferences,
        fromPendingActivations,
        resumeName: resume?.fileName,
        openResume,
        openCertificate,
      }).map((item) => {
        const { key, title, keepExpanded, cardContent, customComponent } = item ?? {};

        return (
          <CollapsibleCard
            key={key}
            title={title}
            keepExpanded={keepExpanded}
            content={(
              <ProfileContent
                successAction={successAction}
                content={cardContent}
                customComponent={customComponent}
              />
            )}
          />
        );
      })}
    </Box>
  );
};

export default PharmacistViewProfile;
